// useTourController — drives the passive auto-tour reel.
//
// The tour cycles through SCENES at a fixed cadence:
//   - 8s dwell per scene (camera framed at the active scene's anchor,
//     HUD panel mounted, ambient audio cross-fading),
//   - 2s transition to the next scene (camera glides via the same
//     smootherstep easing the narrative mode uses),
//   - 70s total (7 scenes × 10s).
//
// User interaction is the off-switch:
//   - mousewheel / touch scroll            → pause
//   - pointer / touch start                → pause
//   - any keydown that the page would act
//     on (non-modifier, non-input)         → pause
//   - mode change away from 'tour'         → stop
//
// After 4s of no further interaction, the tour resumes. While paused,
// the camera holds and the overlay flashes a PAUSED chip. Resuming
// fades the chip out over 600ms.
//
// Window globals exposed for non-React consumers (SceneCameraRig,
// AmbientAudioController, ColorBlockLayer, HUDController):
//   __planetTourSceneIndex   number  0..6
//   __planetTourProgress     number  0..1 (sub-scene progress during
//                                      the 2s transition window)
//   __planetTourPaused       boolean
//   __planetTourActive       boolean  (true while mode === 'tour')

import { useEffect } from 'react';
import { SCENES } from './planetSections';
import { track } from '../utils/analytics';
import { useAmbientAudio, type AmbientAudioController } from './useAmbientAudio';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/** How long the camera dwells at each scene before transitioning. */
const DWELL_MS = 8000;
/** Duration of the transition between two adjacent scenes. */
const TRANSITION_MS = 2000;
/** Total cycle length (dwell + transition) per scene. */
const CYCLE_MS = DWELL_MS + TRANSITION_MS;
/** How long the tour stays paused after the last user interaction. */
const RESUME_DELAY_MS = 4000;
/** Fade-out duration for the PAUSED chip when the tour resumes. */
const RESUME_FADE_MS = 600;
/** Rate at which we fire `tour_tick` analytics (per second of dwell). */
const TICK_ANALYTICS_THROTTLE_MS = 4000;

// ---------------------------------------------------------------------------
// Subscription bus — tiny module-singleton pub/sub so non-React consumers
// can listen without forcing re-renders.
// ---------------------------------------------------------------------------

export interface TourSnapshot {
  /** Which scene the tour is showing right now (0..6). */
  sceneIndex: number;
  /** Where we are within the scene's 0..1 progress. */
  progress: number;
  /** True while the tour has been suspended by user interaction. */
  paused: boolean;
  /** True while the tour is the active mode (paused or not). */
  active: boolean;
}

type TourListener = (s: TourSnapshot) => void;
const tourSnapshotListeners = new Set<TourListener>();

function emitTourSnapshot(s: TourSnapshot) {
  if (typeof window !== 'undefined') {
    window.__planetTourSceneIndex = s.sceneIndex;
    window.__planetTourProgress = s.progress;
    window.__planetTourPaused = s.paused;
  }
  for (const cb of tourSnapshotListeners) {
    try {
      cb(s);
    } catch {
      /* keep the bus alive */
    }
  }
}

// ---------------------------------------------------------------------------
// Imperative controller — exported via the singleton ref so the keyboard
// hook can call .toggle() without going through React.
// ---------------------------------------------------------------------------

export interface TourController {
  /** Start or stop the tour. Called by the T keyboard shortcut and the
   *  ModeToggle pill. */
  toggle: () => void;
  /** Explicitly start the tour (no-op if already running). */
  start: () => void;
  /** Explicitly stop the tour (no-op if not running). */
  stop: () => void;
  /** Latest snapshot. Cheap getter, no event subscription required. */
  getSnapshot: () => TourSnapshot;
  /** Subscribe to snapshot updates. Returns an unsubscribe fn. */
  subscribe: (cb: TourListener) => () => void;
}

declare global {
  interface Window {
    __planetTourController?: TourController;
  }
}

let __tourControllerInstance: TourController | null = null;

export function getTourController(): TourController | null {
  return __tourControllerInstance;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTourController(): void {
  // useAmbientAudio is itself a hook and must be called unconditionally.
  // Calling it inside a ref-initializer branch breaks React's hook order
  // on the second render and can surface in production as deps.length
  // crashes on the following useEffect.
  const audio = useAmbientAudio();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // --- State (refs so rAF loop never re-binds) -----------------------
    let active = false;
    let paused = false;
    let cycleStart = 0;
    let lastInteractionAt = 0;
    let lastTickAnalyticsAt = 0;
    let tickRaf = 0;

    const setMode = (m: 'narrative' | 'exploration' | 'tour') => {
      const ctrl = window.__planetModeController;
      if (ctrl) {
        ctrl.setMode(m);
      } else {
        window.__planetMode = m;
        try {
          window.localStorage.setItem('yhazrin:mode', m);
        } catch {
          /* ignore */
        }
      }
    };
    const currentMode = (): 'narrative' | 'exploration' | 'tour' =>
      window.__planetMode ?? 'narrative';

    // --- Snapshot -----------------------------------------------------
    const snapshot: TourSnapshot = {
      sceneIndex: 0,
      progress: 0,
      paused: false,
      active: false,
    };

    function publish() {
      emitTourSnapshot({ ...snapshot });
    }

    // --- Tour lifecycle ----------------------------------------------
    function startTour() {
      if (active) return;
      active = true;
      paused = false;
      cycleStart = performance.now();
      lastInteractionAt = 0;
      window.__planetTourActive = true;
      snapshot.active = true;
      snapshot.paused = false;
      snapshot.sceneIndex = 0;
      snapshot.progress = 0;
      // Snap scroll to top so the camera framing matches the tour's
      // notion of "scene 0" — the same trick the controller uses when
      // entering tour mode from a non-zero scroll position.
      window.scrollTo({ top: 0, behavior: 'auto' });
      // Force scene-index mirror to 0 so ColorBlockLayer and the HUD
      // panel snap to scene 0 without waiting for the rAF tick.
      window.__planetSceneIndex = 0;
      audio.setScene(0);
      publish();
      track('tour_start', { from: currentMode() });
    }

    function stopTour(reason: 'mode_change' | 'keyboard' | 'manual' | 'unmount') {
      if (!active) return;
      active = false;
      paused = false;
      window.__planetTourActive = false;
      snapshot.active = false;
      snapshot.paused = false;
      publish();
      track('tour_stop', { reason });
    }

    function pauseTour(reason: 'wheel' | 'pointer' | 'key') {
      if (!active || paused) return;
      paused = true;
      lastInteractionAt = performance.now();
      snapshot.paused = true;
      publish();
      track('tour_pause', { reason });
    }

    // --- Main loop ---------------------------------------------------
    function tick() {
      if (!active) {
        tickRaf = 0;
        return;
      }
      const now = performance.now();

      // Resume after the RESUME_DELAY_MS grace period.
      if (paused && lastInteractionAt > 0 && now - lastInteractionAt > RESUME_DELAY_MS) {
        paused = false;
        // Re-anchor the cycle so the resume happens at a clean dwell
        // boundary, not mid-transition.
        cycleStart = now - DWELL_MS;
        snapshot.paused = false;
        publish();
        track('tour_resume');
      }

      if (!paused) {
        const elapsed = (now - cycleStart) % CYCLE_MS;
        const sceneIdx = Math.min(
          SCENES.length - 1,
          Math.floor((now - cycleStart) / CYCLE_MS) % SCENES.length,
        );
        let progress: number;
        if (elapsed < DWELL_MS) {
          // Dwell at the scene anchor — progress stays at 0 (we render
          // the scene's exact camera pose, not a blend).
          progress = 0;
        } else {
          // Transition — progress ramps 0..1 over TRANSITION_MS.
          progress = (elapsed - DWELL_MS) / TRANSITION_MS;
        }
        if (
          sceneIdx !== snapshot.sceneIndex ||
          progress !== snapshot.progress
        ) {
          snapshot.sceneIndex = sceneIdx;
          snapshot.progress = progress;
          // Mirror the scene index onto the bus globals so the
          // narrative-mode consumers (ColorBlockLayer, HUD panels,
          // etc.) see the tour's active scene without a separate bus.
          window.__planetSceneIndex = sceneIdx;
          audio.setScene(sceneIdx);
          publish();
        }
        // Throttled analytics — once per TICK_ANALYTICS_THROTTLE_MS.
        if (now - lastTickAnalyticsAt > TICK_ANALYTICS_THROTTLE_MS) {
          lastTickAnalyticsAt = now;
          const s = SCENES[sceneIdx];
          if (s) {
            track('tour_tick', {
              scene: sceneIdx,
              sceneKey: s.key,
              sceneName: s.name,
            });
          }
        }
      }

      tickRaf = window.requestAnimationFrame(tick);
    }

    // --- Mode watcher — kicks the tour on/off -----------------------
    let lastMode: 'narrative' | 'exploration' | 'tour' =
      window.__planetMode ?? 'narrative';
    const modePoll = window.setInterval(() => {
      const cur = currentMode();
      if (cur === lastMode) return;
      if (cur === 'tour') {
        startTour();
      } else if (lastMode === 'tour') {
        stopTour('mode_change');
      }
      lastMode = cur;
    }, 100);

    // --- User-input listeners — pause on interaction ---------------
    const onWheel = (_e: WheelEvent) => {
      if (!active) return;
      pauseTour('wheel');
    };
    const onPointerDown = (_e: PointerEvent) => {
      if (!active) return;
      pauseTour('pointer');
    };
    const onTouchStart = (_e: TouchEvent) => {
      if (!active) return;
      pauseTour('pointer');
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!active) return;
      // Don't react to typing-in-fields.
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (t.isContentEditable) return;
      }
      // Modifier-only combos (Cmd-R, Ctrl-1) are the browser's.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      pauseTour('key');
    };

    document.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    // --- Imperative controller (singleton) ---------------------------
    const controller: TourController = {
      toggle: () => {
        if (active) {
          stopTour('manual');
          setMode('narrative');
        } else {
          setMode('tour');
          // startTour() will be called by the mode watcher above.
          // We also call it eagerly so the user doesn't perceive a
          // single frame of "narrative → tour → still paused".
          startTour();
        }
      },
      start: () => {
        setMode('tour');
        startTour();
      },
      stop: () => {
        stopTour('manual');
        if (currentMode() === 'tour') setMode('narrative');
      },
      getSnapshot: () => ({ ...snapshot }),
      subscribe: (cb) => {
        tourSnapshotListeners.add(cb);
        // Replay the latest value on subscribe so the consumer doesn't
        // have to poll.
        try {
          cb({ ...snapshot });
        } catch {
          /* keep the bus alive */
        }
        return () => {
          tourSnapshotListeners.delete(cb);
        };
      },
    };
    window.__planetTourController = controller;
    __tourControllerInstance = controller;

    // If we mounted while already in tour mode, kick the loop.
    if (currentMode() === 'tour') {
      startTour();
      tickRaf = window.requestAnimationFrame(tick);
    }

    return () => {
      // --- teardown -----------------------------------------------
      window.cancelAnimationFrame(tickRaf);
      window.clearInterval(modePoll);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('keydown', onKeyDown);
      if (window.__planetTourController === controller) {
        window.__planetTourController = undefined;
      }
      if (__tourControllerInstance === controller) {
        __tourControllerInstance = null;
      }
      if (active) {
        stopTour('unmount');
      }
    };
  }, [audio]);
}

export default useTourController;
